import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createB2BOrder,
  getB2BOrder,
  getNextB2BOrderNumber,
  updateB2BOrder,
  uploadB2BEwayBillDocument,
  uploadB2BInvoiceDocument,
} from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';
import '../ManualOrders/CreateManualOrder.css';
import './B2BOrders.css';

/**
 * Stage 1 of a B2B order — what the B2B team keys in.
 *
 * A wizard rather than one long form: this covers the whole yellow block of the
 * ops spreadsheet, and 30-odd fields on a single screen is how that sheet
 * became hard to fill correctly.
 *
 * The same component serves create and edit. Pass an `orderId` and it loads
 * that order and PATCHes on submit; without one it POSTs a new order.
 *
 * Stage 2 — the AWB, shipper and tracking that ops adds later — is deliberately
 * not here. It lives on the detail screen, because it is a different team
 * working at a different time.
 */

const STEPS = [
  'Order',
  'Vendors & products',
  'Invoice',
  'Pickup',
  'Packaging',
  'Customer & delivery',
  'Review',
];

// Offered as dropdowns and stored verbatim as strings: ops picks the unit the
// vendor quoted, and normalising it server-side would only lose that.
const DIMENSION_UNITS = ['inches', 'cms'];
const WEIGHT_UNITS = ['kgs', 'gms'];

const emptyProduct = () => ({
  key: `p-${Math.random().toString(36).slice(2)}`,
  product_name: '',
  sku: '',
  quantity: '',
  // The line's total as invoiced, tax included — deliberately not a unit
  // price. The invoice's per-unit figure is pre-GST while its total is not, so
  // multiplying one out never matched the other.
  total_amount: '',
});

const emptyVendor = () => ({
  key: `v-${Math.random().toString(36).slice(2)}`,
  vendor_name: '',
  products: [emptyProduct()],
});

const emptyBox = (index) => ({
  key: `b-${Math.random().toString(36).slice(2)}`,
  name: String(index + 1),
  length: '',
  breadth: '',
  height: '',
  unit: 'inches',
  weight: '',
  weight_unit: 'kgs',
});

/* One marker for every required field, so the form says "required" in exactly
   one way. Rendered rather than written into each label by hand. */
const req = <span className="b2bReq" aria-hidden="true">*</span>;

const money = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function B2BOrderForm({ orderId = null }) {
  const navigate = useNavigate();
  const isEdit = Boolean(orderId);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [order, setOrder] = useState({
    order_date: '',
    mode: '',
    invoice_number: '',
    invoice_date: '',
    pickup_company_name: '',
    pickup_address_line_1: '',
    pickup_address_line_2: '',
    pickup_landmark: '',
    pickup_city: '',
    pickup_state: '',
    pickup_pincode: '',
    pickup_country: 'India',
    pickup_gstin: '',
    pickup_poc_name: '',
    pickup_poc_contact: '',
    pickup_poc_email: '',
    customer_name: '',
    customer_contact: '',
    customer_email: '',
    delivery_company_name: '',
    delivery_address_line_1: '',
    delivery_address_line_2: '',
    delivery_landmark: '',
    delivery_city: '',
    delivery_state: '',
    delivery_pincode: '',
    delivery_country: 'India',
    delivery_gstin: '',
    timeline_commitment: '',
    notes: '',
  });

  const [vendors, setVendors] = useState([emptyVendor()]);
  const [boxes, setBoxes] = useState([emptyBox(0)]);

  // The headline figures. Each is autofilled from the lines until someone types
  // in it; `touched` is what stops a later edit to a product from silently
  // undoing what they typed.
  const [invoiceValue, setInvoiceValue] = useState('');
  const [invoiceValueTouched, setInvoiceValueTouched] = useState(false);
  const [totalQuantity, setTotalQuantity] = useState('');
  const [totalQuantityTouched, setTotalQuantityTouched] = useState(false);

  // Preview only: the real number is allocated server-side on save, so an
  // abandoned form never burns one.
  const [numberPreview, setNumberPreview] = useState('');
  // The already-assigned number, when editing. Read-only, so it is kept out
  // of the editable `order` state.
  const [existingNumber, setExistingNumber] = useState('');

  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceUrl, setInvoiceUrl] = useState('');
  // Optional: only needed above the statutory value threshold, and whether
  // to attach one is the admin's call — so nothing here insists on it.
  const [ewayFile, setEwayFile] = useState(null);
  const [ewayUrl, setEwayUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  useTaraTerminalFocus('b2b_order_form', {
    mode: isEdit ? 'editing' : 'creating',
    step: STEPS[step],
    order_number: existingNumber || numberPreview || undefined,
    form: order,
    vendors,
    boxes,
    invoice_value: invoiceValue,
    total_quantity: totalQuantity,
  });

  // --- the next number, when creating -------------------------------------
  useEffect(() => {
    if (isEdit) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getNextB2BOrderNumber();
        if (!cancelled) setNumberPreview(data?.order_number || '');
      } catch {
        // A failed preview is cosmetic — the server still assigns on save.
        if (!cancelled) setNumberPreview('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  // --- load, when editing -------------------------------------------------
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getB2BOrder(orderId);
        if (cancelled) return;
        const o = data.order;
        setOrder({
          order_date: o.order_date || '',
          mode: o.mode || '',
          invoice_number: o.invoice_number || '',
          invoice_date: o.invoice_date || '',
          pickup_company_name: o.pickup_company_name || '',
          pickup_address_line_1: o.pickup_address_line_1 || '',
          pickup_address_line_2: o.pickup_address_line_2 || '',
          pickup_landmark: o.pickup_landmark || '',
          pickup_city: o.pickup_city || '',
          pickup_state: o.pickup_state || '',
          pickup_pincode: o.pickup_pincode || '',
          pickup_country: o.pickup_country || 'India',
          pickup_gstin: o.pickup_gstin || '',
          pickup_poc_name: o.pickup_poc_name || '',
          pickup_poc_contact: o.pickup_poc_contact || '',
          pickup_poc_email: o.pickup_poc_email || '',
          customer_name: o.customer_name || '',
          customer_contact: o.customer_contact || '',
          customer_email: o.customer_email || '',
          delivery_company_name: o.delivery_company_name || '',
          delivery_address_line_1: o.delivery_address_line_1 || '',
          delivery_address_line_2: o.delivery_address_line_2 || '',
          delivery_landmark: o.delivery_landmark || '',
          delivery_city: o.delivery_city || '',
          delivery_state: o.delivery_state || '',
          delivery_pincode: o.delivery_pincode || '',
          delivery_country: o.delivery_country || 'India',
          delivery_gstin: o.delivery_gstin || '',
          timeline_commitment: o.timeline_commitment || '',
          notes: o.notes || '',
        });
        setVendors(
          (o.vendors || []).length
            ? o.vendors.map((v) => ({
                key: v.id,
                vendor_name: v.vendor_name,
                products: (v.products || []).map((p) => ({
                  key: p.id,
                  product_name: p.product_name,
                  sku: p.sku || '',
                  quantity: String(p.quantity ?? ''),
                  total_amount: String(p.total_amount ?? ''),
                })),
              }))
            : [emptyVendor()]
        );
        setBoxes(
          (o.boxes || []).length
            ? o.boxes.map((b, i) => ({
                key: `b-${i}`,
                name: b.name ?? String(i + 1),
                length: b.length ?? '',
                breadth: b.breadth ?? '',
                height: b.height ?? '',
                unit: b.unit || 'inches',
                weight: b.weight ?? '',
                weight_unit: b.weight_unit || 'kgs',
              }))
            : [emptyBox(0)]
        );
        setInvoiceValue(o.invoice_value ?? '');
        setInvoiceValueTouched(Boolean(o.invoice_value_overridden));
        setTotalQuantity(o.total_quantity ?? '');
        setTotalQuantityTouched(Boolean(o.total_quantity_overridden));
        setExistingNumber(o.order_number || '');
        setInvoiceUrl(o.invoice_document_url || '');
        setEwayUrl(o.eway_bill_document_url || '');
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load this order.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, orderId]);

  // --- rollups ------------------------------------------------------------
  const rollup = useMemo(() => {
    const perVendor = vendors.map((v) => {
      const value = v.products.reduce((sum, p) => sum + Number(p.total_amount || 0), 0);
      const qty = v.products.reduce((sum, p) => sum + Number(p.quantity || 0), 0);
      return { value, qty };
    });
    return {
      perVendor,
      value: perVendor.reduce((s, v) => s + v.value, 0),
      qty: perVendor.reduce((s, v) => s + v.qty, 0),
    };
  }, [vendors]);

  // Autofill only while the field is untouched — the whole point of `touched`.
  useEffect(() => {
    if (!invoiceValueTouched) setInvoiceValue(rollup.value ? rollup.value.toFixed(2) : '');
  }, [rollup.value, invoiceValueTouched]);

  useEffect(() => {
    if (!totalQuantityTouched) setTotalQuantity(rollup.qty ? String(rollup.qty) : '');
  }, [rollup.qty, totalQuantityTouched]);

  // --- field helpers ------------------------------------------------------
  const setField = (name) => (e) => setOrder((prev) => ({ ...prev, [name]: e.target.value }));

  const updateVendor = (vKey, patch) =>
    setVendors((prev) => prev.map((v) => (v.key === vKey ? { ...v, ...patch } : v)));

  const updateProduct = (vKey, pKey, patch) =>
    setVendors((prev) =>
      prev.map((v) =>
        v.key === vKey
          ? {
              ...v,
              products: v.products.map((p) => (p.key === pKey ? { ...p, ...patch } : p)),
            }
          : v
      )
    );

  const updateBox = (key, patch) =>
    setBoxes((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));

  // --- validation ---------------------------------------------------------
  const vendorsComplete = useCallback(
    () =>
      vendors.length > 0 &&
      vendors.every(
        (v) =>
          v.vendor_name.trim() &&
          v.products.length > 0 &&
          v.products.every(
            (p) =>
              p.product_name.trim() &&
              String(p.quantity).trim() &&
              Number(p.quantity) > 0 &&
              String(p.total_amount).trim() &&
              Number(p.total_amount) >= 0
          )
      ),
    [vendors]
  );

  /* What each step will not let you leave without. Mirrors REQUIRED_FIELDS on
     the serializer: the form should refuse what the API would refuse, rather
     than letting someone reach Review and be told no. */
  const filled = useCallback((...names) => names.every((n) => String(order[n] || '').trim()), [
    order,
  ]);

  const stepValid = useCallback(
    (index) => {
      switch (index) {
        case 0:
          // Nothing required here now: the number is server-assigned and the
          // date and mode are optional.
          return true;
        case 1:
          return vendorsComplete();
        case 2:
          return filled('invoice_number');
        case 3:
          return filled(
            'pickup_address_line_1',
            'pickup_city',
            'pickup_state',
            'pickup_pincode',
            'pickup_gstin',
            'pickup_poc_name',
            'pickup_poc_contact'
          );
        case 4:
          // Dimensions and weight are what a courier prices on.
          return (
            boxes.length > 0 &&
            boxes.every(
              (b) =>
                String(b.length).trim() &&
                String(b.breadth).trim() &&
                String(b.height).trim() &&
                String(b.weight).trim()
            )
          );
        case 5:
          return filled(
            'customer_name',
            'customer_contact',
            'customer_email',
            'delivery_address_line_1',
            'delivery_city',
            'delivery_state',
            'delivery_pincode',
            'delivery_gstin'
          );
        default:
          return true;
      }
    },
    [filled, vendorsComplete, boxes]
  );

  const canSubmit = [0, 1, 2, 3, 4, 5].every(stepValid) && !submitting;

  // --- submit -------------------------------------------------------------
  const buildPayload = () => {
    const payload = { ...order };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '') delete payload[k];
    });
    payload.vendors = vendors.map((v) => ({
      vendor_name: v.vendor_name.trim(),
      products: v.products.map((p) => ({
        product_name: p.product_name.trim(),
        sku: p.sku.trim(),
        quantity: Number(p.quantity),
        total_amount: String(p.total_amount),
      })),
    }));

    const filledBoxes = boxes.filter(
      (b) => b.length || b.breadth || b.height || b.weight
    );
    payload.boxes = filledBoxes.map((b, i) => ({
      name: String(b.name || i + 1),
      length: b.length === '' ? null : Number(b.length),
      breadth: b.breadth === '' ? null : Number(b.breadth),
      height: b.height === '' ? null : Number(b.height),
      unit: b.unit,
      weight: b.weight === '' ? null : Number(b.weight),
      weight_unit: b.weight_unit,
    }));
    if (filledBoxes.length) payload.box_count = filledBoxes.length;

    // Only send a figure the operator actually claimed; otherwise let the
    // server's rollup fill it, so the two never disagree.
    if (invoiceValueTouched && String(invoiceValue).trim()) {
      payload.invoice_value = String(invoiceValue);
    }
    if (totalQuantityTouched && String(totalQuantity).trim()) {
      payload.total_quantity = Number(totalQuantity);
    }
    return payload;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = buildPayload();
      const data = isEdit
        ? await updateB2BOrder(orderId, payload)
        : await createB2BOrder(payload);
      const saved = data.order;

      // Each document uploads on its own, so one failing never costs the other.
      const uploads = [
        [invoiceFile, uploadB2BInvoiceDocument, 'invoice'],
        [ewayFile, uploadB2BEwayBillDocument, 'e-way bill'],
      ].filter(([file]) => file);
      if (uploads.length) setUploading(true);
      for (const [file, upload, label] of uploads) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await upload(saved.id, file);
        } catch (uploadErr) {
          // The order is saved; say so plainly rather than implying it failed.
          showToast({
            type: 'error',
            message: `Order saved, but the ${label} did not upload: ${
              uploadErr?.message || 'unknown error'
            }`,
          });
        }
      }
      setUploading(false);

      showToast({
        type: 'success',
        message: `${saved.order_number} ${isEdit ? 'updated' : 'created'}.`,
      });
      navigate('/suspire-terminal/b2b-orders');
    } catch (err) {
      const message =
        err?.errors
          ? Object.entries(err.errors)
              .map(([field, msgs]) => `${field}: ${[].concat(msgs).join(', ')}`)
              .join(' · ')
          : err?.message || 'Something went wrong.';
      setError(message);
      showToast({ type: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="labelsContainer b2bWizard b2bPage">
        <p className="b2bMuted">Loading…</p>
      </div>
    );
  }

  // --- steps --------------------------------------------------------------
  const renderOrder = () => (
    <>
      <div className="grid">
        <label className="field">
          <span>Order number</span>
          <input
            value={isEdit ? existingNumber : numberPreview}
            readOnly
            disabled
            placeholder="Assigned when you save"
          />
          <small className="helpText">
            {isEdit
              ? 'Assigned when the order was created and never changes.'
              : 'Generated on save as B2B{MMYY}-{NNN}. The preview can move if someone else saves first.'}
          </small>
        </label>
        <label className="field">
          <span>Order date</span>
          <input type="date" value={order.order_date} onChange={setField('order_date')} />
        </label>
        <label className="field">
          <span>Mode</span>
          <input
            value={order.mode}
            onChange={setField('mode')}
            placeholder="e.g. Prozo-PTL, Porter"
          />
          <small className="helpText">How the goods will move.</small>
        </label>
      </div>
      <label className="field gridFull">
        <span>Internal notes</span>
        <textarea
          rows={3}
          value={order.notes}
          onChange={setField('notes')}
          placeholder="For the team. Never sent to the courier or the customer."
        />
      </label>
    </>
  );

  const renderVendors = () => (
    <>
      <p className="b2bStepIntro">
        Vendor and product names are free text, kept for audit — they are not linked to
        terminal vendors. Totals below roll up into the invoice value and quantity.
      </p>

      {vendors.map((vendor, vIndex) => (
        <section className="b2bVendorCard" key={vendor.key}>
          <header className="b2bVendorCardHead">
            <span className="b2bVendorBadge">{vIndex + 1}</span>
            <span className="b2bVendorCardTitle">
              {vendor.vendor_name.trim() || `Vendor ${vIndex + 1}`}
            </span>
            {vendors.length > 1 && (
              <button
                type="button"
                className="b2bVendorRemove"
                title="Remove this vendor"
                aria-label={`Remove vendor ${vIndex + 1}`}
                onClick={() => setVendors((prev) => prev.filter((v) => v.key !== vendor.key))}
              >
                ×
              </button>
            )}
          </header>

          <div className="b2bVendorCardBody">
            <label className="field b2bVendorNameField">
              <span>Vendor name {req}</span>
              <input
                value={vendor.vendor_name}
                onChange={(e) => updateVendor(vendor.key, { vendor_name: e.target.value })}
                placeholder="e.g. The Label Gaia"
              />
            </label>

            <div className="b2bLines">
              <div className="b2bLinesHeader">
                <h4>Products</h4>
                <span className="b2bLinesCount">
                  {vendor.products.length} line{vendor.products.length === 1 ? '' : 's'}
                </span>
                <span className="b2bLinesQty">
                  {rollup.perVendor[vIndex]?.qty || 0} units
                </span>
              </div>

              {vendor.products.map((product, pIndex) => (
                <div className="b2bLineCard" key={product.key}>
                  <div className="b2bLineCardHead">
                    <span className="b2bLineBadge">{pIndex + 1}</span>
                    <span className="b2bLineTitle">
                      {product.product_name.trim() || 'New product'}
                    </span>
                    {vendor.products.length > 1 && (
                      <button
                        type="button"
                        className="b2bLineRemove"
                        title="Remove this product"
                        aria-label={`Remove product ${pIndex + 1}`}
                        onClick={() =>
                          updateVendor(vendor.key, {
                            products: vendor.products.filter((p) => p.key !== product.key),
                          })
                        }
                      >
                        ×
                      </button>
                    )}
                  </div>

                  <div className="b2bLineFields">
                    <label className="field">
                      <span>Product {req}</span>
                      <input
                        value={product.product_name}
                        onChange={(e) =>
                          updateProduct(vendor.key, product.key, {
                            product_name: e.target.value,
                          })
                        }
                        placeholder="e.g. Diaries"
                      />
                    </label>
                    <label className="field">
                      <span>SKU</span>
                      <input
                        value={product.sku}
                        onChange={(e) =>
                          updateProduct(vendor.key, product.key, { sku: e.target.value })
                        }
                        placeholder="Optional"
                      />
                    </label>
                    <label className="field b2bFieldNarrow">
                      <span>Qty {req}</span>
                      <input
                        type="number"
                        min="1"
                        value={product.quantity}
                        onChange={(e) =>
                          updateProduct(vendor.key, product.key, { quantity: e.target.value })
                        }
                        placeholder="0"
                      />
                    </label>
                    <label className="field b2bFieldNarrow">
                      <span>Total amount (₹) {req}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={product.total_amount}
                        onChange={(e) =>
                          updateProduct(vendor.key, product.key, {
                            total_amount: e.target.value,
                          })
                        }
                        placeholder="0.00"
                      />
                      <small className="helpText">For the whole line, as invoiced.</small>
                    </label>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="b2bAddLine"
                onClick={() =>
                  updateVendor(vendor.key, {
                    products: [...vendor.products, emptyProduct()],
                  })
                }
              >
                <span className="b2bAddLineIcon">+</span>
                Add product
              </button>
            </div>
          </div>

          <footer className="b2bVendorCardFoot">
            <span>Vendor subtotal</span>
            <strong>₹{money(rollup.perVendor[vIndex]?.value || 0)}</strong>
          </footer>
        </section>
      ))}

      <div className="b2bVendorActions">
        <button
          type="button"
          className="b2bAddVendor"
          onClick={() => setVendors((prev) => [...prev, emptyVendor()])}
        >
          <span className="b2bAddLineIcon">+</span>
          Add vendor
        </button>
        <div className="b2bGrandTotal">
          <span className="b2bGrandLabel">Order total</span>
          <strong>₹{money(rollup.value)}</strong>
          <span className="b2bGrandQty">{rollup.qty} units</span>
        </div>
      </div>
    </>
  );

  const renderInvoice = () => (
    <>
      <div className="grid">
        <label className="field">
          <span>Invoice number {req}</span>
          <input value={order.invoice_number} onChange={setField('invoice_number')} />
        </label>
        <label className="field">
          <span>Invoice date</span>
          <input type="date" value={order.invoice_date} onChange={setField('invoice_date')} />
        </label>
      </div>

      <div className="grid">
        <label className="field">
          <span>Invoice value</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={invoiceValue}
            onChange={(e) => {
              setInvoiceValueTouched(true);
              setInvoiceValue(e.target.value);
            }}
          />
          <small className="helpText">
            {invoiceValueTouched ? (
              <>
                Overridden. Products add up to ₹{money(rollup.value)}.{' '}
                <button
                  type="button"
                  className="b2bLinkBtn"
                  onClick={() => setInvoiceValueTouched(false)}
                >
                  Use that instead
                </button>
              </>
            ) : (
              'Filled in from the products. Type here to override it.'
            )}
          </small>
        </label>

        <label className="field">
          <span>Total quantity</span>
          <input
            type="number"
            min="0"
            value={totalQuantity}
            onChange={(e) => {
              setTotalQuantityTouched(true);
              setTotalQuantity(e.target.value);
            }}
          />
          <small className="helpText">
            {totalQuantityTouched ? (
              <>
                Overridden. Products add up to {rollup.qty}.{' '}
                <button
                  type="button"
                  className="b2bLinkBtn"
                  onClick={() => setTotalQuantityTouched(false)}
                >
                  Use that instead
                </button>
              </>
            ) : (
              'Filled in from the products. Type here to override it.'
            )}
          </small>
        </label>
      </div>

      <div className="field gridFull">
        <span>Invoice document {req}</span>
        {invoiceUrl && !invoiceFile && (
          <p className="b2bUploadedFile">
            <a href={invoiceUrl} target="_blank" rel="noreferrer">
              View the uploaded invoice
            </a>
          </p>
        )}
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
        />
        <small className="helpText">
          PDF, up to 10MB. Uploaded when you save{isEdit ? '' : ' the order'}.
          {invoiceFile ? ` Selected: ${invoiceFile.name}` : ''}
        </small>
      </div>

      <div className="field gridFull">
        <span>E-way bill (optional)</span>
        {ewayUrl && !ewayFile && (
          <p className="b2bUploadedFile">
            <a href={ewayUrl} target="_blank" rel="noreferrer">
              View the uploaded e-way bill
            </a>
          </p>
        )}
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setEwayFile(e.target.files?.[0] || null)}
        />
        <small className="helpText">
          Attach one when the consignment needs it. PDF, up to 10MB.
          {ewayFile ? ` Selected: ${ewayFile.name}` : ''}
        </small>
      </div>
    </>
  );

  /* An address block, used for both ends of the consignment. Both carry a GSTIN:
     a B2B movement is between two registered businesses, and the sheet recorded
     one on every row at both ends. */
  const renderAddress = (prefix, { poc = false } = {}) => (
    <>
      <div className="grid">
        <label className="field">
          <span>Company name</span>
          <input
            value={order[`${prefix}_company_name`]}
            onChange={setField(`${prefix}_company_name`)}
            placeholder="e.g. Fireflies International Pvt Ltd"
          />
        </label>
        <label className="field">
          <span>GSTIN {req}</span>
          <input
            value={order[`${prefix}_gstin`]}
            onChange={setField(`${prefix}_gstin`)}
            placeholder="e.g. 27AAFCF6204R1Z7"
            maxLength={15}
          />
        </label>
      </div>

      <label className="field gridFull">
        <span>Address line 1 {req}</span>
        <input
          value={order[`${prefix}_address_line_1`]}
          onChange={setField(`${prefix}_address_line_1`)}
          placeholder="Building, street"
        />
      </label>

      <div className="grid">
        <label className="field">
          <span>Address line 2</span>
          <input
            value={order[`${prefix}_address_line_2`]}
            onChange={setField(`${prefix}_address_line_2`)}
            placeholder="Area, locality"
          />
        </label>
        <label className="field">
          <span>Landmark</span>
          <input
            value={order[`${prefix}_landmark`]}
            onChange={setField(`${prefix}_landmark`)}
            placeholder="Optional"
          />
        </label>
      </div>

      <div className="grid">
        <label className="field">
          <span>City {req}</span>
          <input value={order[`${prefix}_city`]} onChange={setField(`${prefix}_city`)} />
        </label>
        <label className="field">
          <span>State {req}</span>
          <input value={order[`${prefix}_state`]} onChange={setField(`${prefix}_state`)} />
        </label>
        <label className="field">
          <span>Pincode {req}</span>
          <input
            value={order[`${prefix}_pincode`]}
            onChange={setField(`${prefix}_pincode`)}
            maxLength={10}
          />
        </label>
        <label className="field">
          <span>Country</span>
          <input value={order[`${prefix}_country`]} onChange={setField(`${prefix}_country`)} />
        </label>
      </div>

      {poc && (
        <>
          <h4 className="b2bSubHead">Pickup contact</h4>
          <div className="grid">
            <label className="field">
              <span>POC name {req}</span>
              <input value={order.pickup_poc_name} onChange={setField('pickup_poc_name')} />
            </label>
            <label className="field">
              <span>POC contact {req}</span>
              <input
                value={order.pickup_poc_contact}
                onChange={setField('pickup_poc_contact')}
              />
            </label>
            <label className="field">
              <span>POC email</span>
              <input
                type="email"
                value={order.pickup_poc_email}
                onChange={setField('pickup_poc_email')}
              />
            </label>
          </div>
        </>
      )}
    </>
  );

  const renderPickup = () => (
    <>
      <p className="b2bStepIntro">
        Where the goods are collected from. The courier needs city, state and pincode to
        book the lane, and the GSTIN for the e-way bill.
      </p>
      {renderAddress('pickup', { poc: true })}
    </>
  );

  const renderPackaging = () => (
    <>
      <p className="b2bStepIntro">
        One card per box. Dimensions and weight are required — they are what the courier
        prices on. Pick the unit the vendor quoted; it is stored exactly as you choose it.
      </p>

      <div className="b2bLinesHeader">
        <h4>Boxes</h4>
        <span className="b2bLinesCount">
          {boxes.length} box{boxes.length === 1 ? '' : 'es'}
        </span>
      </div>

      {boxes.map((box, index) => (
        <div className="b2bLineCard b2bBoxCard" key={box.key}>
          <div className="b2bLineCardHead">
            <span className="b2bLineBadge">{index + 1}</span>
            <span className="b2bLineTitle">Box {box.name || index + 1}</span>
            <span className="b2bLineAmount">
              {box.length && box.breadth && box.height
                ? `${box.length} × ${box.breadth} × ${box.height} ${box.unit}`
                : '—'}
            </span>
            {boxes.length > 1 && (
              <button
                type="button"
                className="b2bLineRemove"
                title="Remove this box"
                aria-label={`Remove box ${index + 1}`}
                onClick={() => setBoxes((prev) => prev.filter((b) => b.key !== box.key))}
              >
                ×
              </button>
            )}
          </div>

          <div className="b2bBoxFields">
            <label className="field">
              <span>Box name</span>
              <input
                value={box.name}
                onChange={(e) => updateBox(box.key, { name: e.target.value })}
                placeholder={String(index + 1)}
              />
            </label>
            <label className="field b2bFieldNarrow">
              <span>Length {req}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={box.length}
                onChange={(e) => updateBox(box.key, { length: e.target.value })}
                placeholder="0"
              />
            </label>
            <label className="field b2bFieldNarrow">
              <span>Breadth {req}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={box.breadth}
                onChange={(e) => updateBox(box.key, { breadth: e.target.value })}
                placeholder="0"
              />
            </label>
            <label className="field b2bFieldNarrow">
              <span>Height {req}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={box.height}
                onChange={(e) => updateBox(box.key, { height: e.target.value })}
                placeholder="0"
              />
            </label>
            <label className="field">
              <span>Unit</span>
              <select
                value={box.unit}
                onChange={(e) => updateBox(box.key, { unit: e.target.value })}
              >
                {DIMENSION_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </label>
            <label className="field b2bFieldNarrow">
              <span>Weight {req}</span>
              <input
                type="number"
                min="0"
                step="0.001"
                value={box.weight}
                onChange={(e) => updateBox(box.key, { weight: e.target.value })}
                placeholder="0"
              />
            </label>
            <label className="field">
              <span>Weight unit</span>
              <select
                value={box.weight_unit}
                onChange={(e) => updateBox(box.key, { weight_unit: e.target.value })}
              >
                {WEIGHT_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="b2bAddLine"
        onClick={() => setBoxes((prev) => [...prev, emptyBox(prev.length)])}
      >
        <span className="b2bAddLineIcon">+</span>
        Add box
      </button>
    </>
  );

  const renderDelivery = () => (
    <>
      <p className="b2bStepIntro">
        Who receives the consignment, and where.
      </p>

      <h4 className="b2bSubHead">Customer</h4>
      <div className="grid">
        <label className="field">
          <span>Customer name {req}</span>
          <input value={order.customer_name} onChange={setField('customer_name')} />
        </label>
        <label className="field">
          <span>Contact {req}</span>
          <input value={order.customer_contact} onChange={setField('customer_contact')} />
        </label>
        <label className="field">
          <span>Email {req}</span>
          <input
            type="email"
            value={order.customer_email}
            onChange={setField('customer_email')}
          />
        </label>
      </div>

      <h4 className="b2bSubHead">Delivery address</h4>
      {renderAddress('delivery')}

      <label className="field b2bTimeline">
        <span>Timeline commitment</span>
        <input
          value={order.timeline_commitment}
          onChange={setField('timeline_commitment')}
          placeholder="e.g. 3-5 days"
        />
        <small className="helpText">What was promised, in words.</small>
      </label>
    </>
  );

  const renderReview = () => (
    <div className="b2bReview">
      <dl className="b2bReviewGrid">
        <div>
          <dt>Order</dt>
          <dd>{(isEdit ? existingNumber : numberPreview) || 'Assigned on save'}</dd>
        </div>
        <div>
          <dt>Date</dt>
          <dd>{order.order_date || '—'}</dd>
        </div>
        <div>
          <dt>Mode</dt>
          <dd>{order.mode || '—'}</dd>
        </div>
        <div>
          <dt>Invoice</dt>
          <dd>{order.invoice_number || '—'}</dd>
        </div>
        <div>
          <dt>Invoice value</dt>
          <dd>
            ₹{money(invoiceValue)}
            {invoiceValueTouched && (
              <span className="b2bOverrideNote"> overridden · computed ₹{money(rollup.value)}</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Total quantity</dt>
          <dd>
            {totalQuantity || 0}
            {totalQuantityTouched && (
              <span className="b2bOverrideNote"> overridden · computed {rollup.qty}</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Customer</dt>
          <dd>{order.customer_name || '—'}</dd>
        </div>
        <div>
          <dt>Deliver to</dt>
          <dd>
            {[order.delivery_city, order.delivery_state, order.delivery_pincode]
              .filter(Boolean)
              .join(', ') || '—'}
          </dd>
        </div>
        <div>
          <dt>Boxes</dt>
          <dd>{boxes.filter((b) => b.length || b.weight).length || 0}</dd>
        </div>
        <div>
          <dt>Invoice PDF</dt>
          <dd>{invoiceFile ? invoiceFile.name : invoiceUrl ? 'Uploaded' : '—'}</dd>
        </div>
        <div>
          <dt>E-way bill</dt>
          <dd>{ewayFile ? ewayFile.name : ewayUrl ? 'Uploaded' : '—'}</dd>
        </div>
      </dl>

      <h3 className="b2bReviewHead">Vendors</h3>
      {vendors.map((v, i) => (
        <div className="b2bReviewVendor" key={v.key}>
          <strong>{v.vendor_name || `Vendor ${i + 1}`}</strong>
          <span>
            {v.products.length} product{v.products.length === 1 ? '' : 's'} ·{' '}
            {rollup.perVendor[i]?.qty || 0} units · ₹{money(rollup.perVendor[i]?.value || 0)}
          </span>
        </div>
      ))}

      <p className="b2bReviewNote">
        Shipment details — AWB, shipper and tracking — are added by ops later, from this
        order's page.
      </p>

      {error && <p className="fieldError">{error}</p>}
    </div>
  );

  const renderStep = () => {
    if (step === 0) return renderOrder();
    if (step === 1) return renderVendors();
    if (step === 2) return renderInvoice();
    if (step === 3) return renderPickup();
    if (step === 4) return renderPackaging();
    if (step === 5) return renderDelivery();
    return renderReview();
  };

  return (
    <div className="labelsContainer b2bWizard b2bPage">
      <div className="labelsHeader">
        <h1 className="labelsTitle">{isEdit ? 'Edit B2B Order' : 'New B2B Order'}</h1>
        <p className="labelsSubtitle">
          A bulk order raised by the B2B team. Ops adds the shipment once it is booked.
        </p>
      </div>

      <div className="layout">
        <nav className="stepper" aria-label="B2B order steps">
          {STEPS.map((name, index) => (
            <button
              type="button"
              key={name}
              className={`step ${index === step ? 'stepCurrent' : ''} ${
                index < step ? 'stepDone' : ''
              }`}
              // When editing, every step is already filled, so let them jump.
              disabled={!isEdit && index > step}
              onClick={() => setStep(index)}
            >
              <span className="stepIndex">{index < step ? '✓' : index + 1}</span>
              {name}
            </button>
          ))}
        </nav>

        <section className="panel">
          <h2 className="panelTitle">
            {step + 1}. {STEPS[step]}
          </h2>
          <div className="panelBody">{renderStep()}</div>

          <div className="actions">
            <button
              type="button"
              className="btn btnGhost"
              onClick={() => navigate('/suspire-terminal/b2b-orders')}
            >
              Cancel
            </button>
            {step > 0 && (
              <button type="button" className="btn btnGhost" onClick={() => setStep(step - 1)}>
                Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                className="btn btnPrimary"
                disabled={!stepValid(step)}
                onClick={() => setStep(step + 1)}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                className="btn btnPrimary"
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {submitting || uploading
                  ? 'Saving…'
                  : isEdit
                  ? 'Save changes'
                  : 'Create order'}
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
